"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { formatTranslatedCatalogTaxonomyLabel } from "@/lib/music-taxonomy";

import { getSearchDiscoveryQueryOptions } from "./search-discovery-query";

interface SearchDiscoveryGenreStyleChipsProps {
  enabled: boolean;
}

export function SearchDiscoveryGenreStyleChips({ enabled }: SearchDiscoveryGenreStyleChipsProps) {
  const t = useTranslations("globals.search");
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");
  const query = useQuery(getSearchDiscoveryQueryOptions({ enabled }));

  if (!enabled) return null;

  if (query.isLoading) {
    return (
      <div>
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 sm:mb-4">
          {t("discoverGenreStyle")}
        </h3>
        <div className="flex flex-wrap gap-2.5 sm:gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="h-11 w-44 sm:w-52 rounded-full bg-secondary/80 animate-pulse border border-border/50"
            />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data?.length) {
    return null;
  }

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 sm:mb-4">
        {t("discoverGenreStyle")}
      </h3>
      <div className="flex flex-wrap gap-2.5 sm:gap-3">
        {query.data.slice(0, 12).map((pair) => {
          const label = `${formatTranslatedCatalogTaxonomyLabel(
            pair.genre,
            tg,
          )} / ${formatTranslatedCatalogTaxonomyLabel(pair.style, ts)}`;
          return (
            <Link
              key={`${pair.genre}\0${pair.style}`}
              to="/search"
              search={{ genre: pair.genre, style: pair.style }}
              rel="nofollow"
              className="inline-flex min-h-11 items-center px-4 py-2.5 text-base leading-snug font-medium rounded-full border border-border bg-secondary/60 text-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-colors"
            >
              {label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
